import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { EditIcon, GalleryIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { HostListItem, ToggleHostStatusPayload } from '@/services/hostManagement/hostManagement.types';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import { Button } from 'ui/button';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

enum status {
  ACTIVE = 'A',
}

export function getColumns(
  mutate: (payload: ToggleHostStatusPayload) => Promise<unknown>,
  openConfirmation: OpenConfirmationProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
): ColumnDef<HostListItem>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'position',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.position')} />
      ),
      cell: ({ row }) => {
        return <div className="font-medium truncate max-w-[20rem]">{row.getValue('position')}</div>;
      },
      enableHiding: false,
    },
    {
      accessorKey: 'hostName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.staffName')} />
      ),
      cell: ({ row }) => <div>{row.getValue('hostName')}</div>,
      enableHiding: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.status')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {row.getValue('status') === status.ACTIVE ? (
              <div className="text-success">
                <Translation id={'common.page.table.active'} />
              </div>
            ) : (
              <div className="text-destructive-text">
                <Translation id={'common.page.table.blocked'} />
              </div>
            )}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.createdDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium whitespace-nowrap">
            {formatDate(row.getValue('createdDate'), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.updatedDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium whitespace-nowrap">
            {formatDate(row.getValue('updatedDate'), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.action')} />
      ),

      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {row.getValue('status') === status.ACTIVE && (
            <Can rule={{ allOf: [PERMS.host.detailBasicView] }}>
              <Link
                to="/store-information/basic-stores/host-management/$clubid/$manage-host"
                params={{ clubid: row.original.clubId, 'manage-host': row.original.hostCode }}
                search={(search) => search}
              >
                <EditIcon className="text-foreground-subdued cursor-pointer size-4" />
              </Link>
            </Can>
          )}
          {row.getValue('status') === status.ACTIVE && (
            <Can rule={{ allOf: [PERMS.host.detailBasicView] }}>
              <Link
                to="/store-information/basic-stores/host-management/$clubid/gallery/$host-gallery"
                params={{ clubid: row.original.clubId, 'host-gallery': row.original.hostId }}
                search={(search) => search}
              >
                <GalleryIcon className="text-warning cursor-pointer size-4" />
              </Link>
            </Can>
          )}
          <Can rule={{ allOf: [PERMS.host.basic.block] }}>
            <Button
              variant={'ghost'}
              className="p-0 m-0"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description:
                    row.getValue('status') === 'A'
                      ? t('common.page.modal.blockMessage')
                      : t('common.page.modal.unblockMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    await mutate({
                      clubId: row.original.clubId,
                      hostId: row.original.hostId,
                      status: row.getValue('status') === 'A' ? 'B' : 'A',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === 'A' ? (
                <LockCloseIcon className="text-destructive-text size-4" />
              ) : (
                <LockOpenIcon className="text-link size-4" />
              )}
            </Button>
          </Can>
          {row.getValue('status') === status.ACTIVE && (
            <Can rule={{ allOf: [PERMS.host.basic.block] }}>
              <Trash2
                size={16}
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: t('common.page.modal.deleteStaffMessage', { staffName: row.getValue('hostName') }),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    onAction: async () => {
                      await mutate({
                        clubId: row.original.clubId,
                        hostId: row.original.hostId,
                        status: 'D',
                      });
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'destructive',
                  });
                }}
                className="cursor-pointer text-destructive-text hover:text-destructive-text ml-2"
              />
            </Can>
          )}
        </div>
      ),
      size: 40,
    },
  ];
}
